/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { Controller } from "react-hook-form";

import { DatePicker, Icon, Label, TooltipHost } from "@fluentui/react";

import { FlexCol, FlexRow } from "Components/Common/Layout/Layout";
import { HookErrorMessage } from "Components/ReactHookForm/HookErrorMessage/HookErrorMessage";
import { DayPickerStrings } from "Constants/DateConstants";
import { isDateEmpty } from "Utility/FormatHelper";

import { datePickerStyles, getIconStyles } from "./DatePickerController.styles";
import { IDatePickerControllerProps } from "./IDatePickerControllerProps";

/**
 * DatePickerController.
 * @component
 * @param {IDatePickerControllerProps} props The component props.
 * @returns  {React.ReactElement} DatePickerController
 */
const DatePickerController: (props: IDatePickerControllerProps) => React.ReactElement = (
  props: IDatePickerControllerProps
): React.ReactElement => {
  const {
    control,
    errors,
    hasTooltip,
    inputName,
    isDisabled,
    isRequired,
    labelStyles,
    labelDisplayName,
    placeholderText,
    toolTipDisplayText,
    startDate,
    minDate,
    maxDate,
  } = props;

  const checkedStartDate = !!startDate && isDateEmpty(startDate) ? undefined : startDate;

  return (
    <FlexCol>
      <FlexRow>
        <Label htmlFor={inputName} required={isRequired} styles={labelStyles}>
          {labelDisplayName}
        </Label>
        {hasTooltip && (
          <TooltipHost id={inputName} content={toolTipDisplayText || labelDisplayName}>
            <Icon
              aria-label="information"
              iconName="info"
              role="img"
              styles={getIconStyles(!!isRequired)}
              tabIndex={0}
            />
          </TooltipHost>
        )}
      </FlexRow>
      <FlexRow>
        <Controller
          name={inputName}
          control={control}
          defaultValue={checkedStartDate} // I had to add this to preserve the default value, the way it is in Maven.Frontend did not work.
          render={({ field }): React.ReactElement => (
            <DatePicker
              placeholder={placeholderText}
              ariaLabel={labelDisplayName}
              styles={datePickerStyles}
              value={isDateEmpty(field.value) ? undefined : field.value}
              onSelectDate={field.onChange}
              disabled={isDisabled}
              minDate={minDate}
              maxDate={maxDate}
              strings={DayPickerStrings}
            />
          )}
        />
      </FlexRow>
      <HookErrorMessage errors={errors} inputName={inputName} />
    </FlexCol>
  );
};

export default DatePickerController;
